/**
 * ServiceOrdersEmbeddedList - Aba O.S dos modais (Veículo e Cliente)
 *
 * Reutiliza `ServiceOrdersTable` e `useServiceOrdersList` com filtro fixo por
 * veículo e/ou cliente (validado no servidor). Busca, situação, ordenação e
 * paginação iguais à listagem oficial; sem KPIs e atalhos (espaço do modal).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-16 v1.6.0 - Filtros de situação alinhados à direita, junto ao Atualizar.
 *   RELEASE: 2026-10-14 v1.5.0 - Props opcionais providerId (filtro fixo por prestador) e
 *            onKpisChange (cards do modal do prestador).
 *   RELEASE: 2026-10-09 v1.4.0 - Filtro de situação deixa o Select e passa ao
 *            padrão de pílulas da aba Vistorias (bolinha + nome + contador),
 *            com estados finais e "Atrasadas" no menu "..."; contadores usam
 *            os KPIs já retornados pelo servidor (sem nova consulta).
 *   RELEASE: 2026-10-09 v1.0.0 - Versão inicial (aba O.S do modal de Veículo).
 */
import { useEffect, useState } from 'react';
import { MoreHorizontal, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { LIST_FILTERS_INPUT_CLASS } from '@/components/layout/list-page';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SERVICE_ORDER_STATUS_LABEL, useServiceOrdersList, type ServiceOrderKpis, type ServiceOrderSortField, type ServiceOrderStatus, type ServiceOrdersFilters } from '@/hooks/useServiceOrders';
import { ServiceOrdersTable } from '@/components/service-orders/ServiceOrdersTable';
import { ServiceOrderDetailsModal } from '@/components/service-orders/ServiceOrderDetailsModal';
import { cn } from '@/lib/utils';

/** Valor de filtro de situação aceito pela listagem (inclui o especial "overdue"). */
type StatusFilter = NonNullable<ServiceOrdersFilters['status']>;

/** Cor da bolinha por situação (mesmos tons semânticos do badge de situação da OS). */
const STATUS_DOT: Record<ServiceOrderStatus, string> = {
  open: 'bg-primary',
  scheduled: 'bg-info',
  in_progress: 'bg-primary',
  awaiting_review: 'bg-warning',
  in_correction: 'bg-warning',
  completed: 'bg-success',
  cancelled: 'bg-muted-foreground',
  awaiting_shipment: 'bg-info',
  shipment_in_transit: 'bg-info',
  shipment_delivered: 'bg-success',
  to_reschedule: 'bg-warning',
};

/** Situações "em aberto" sempre visíveis; estados finais ficam no menu "..." (padrão da aba Vistorias). */
const VISIBLE_STATUS: ServiceOrderStatus[] = ['open', 'scheduled'];
const HIDDEN_STATUS: ServiceOrderStatus[] = ['awaiting_shipment', 'shipment_in_transit', 'shipment_delivered', 'to_reschedule', 'in_progress', 'awaiting_review', 'in_correction', 'completed', 'cancelled'];

interface Props {
  vehicleId?: string | null;
  customerId?: string | null;
  /** v1.5.0: filtro fixo por prestador (modal de detalhes da Pessoa) */
  providerId?: string | null;
  /** Filtro fixo por lista de O.S. (aba Vínculos da remessa) */
  ids?: string[] | null;
  /** v1.7.0: filtro fixo por negócio (aba Ordens de serviço do modal do Negócio) */
  dealId?: string | null;
  /** Informa o total ao pai (contador da aba) */
  onTotalChange?: (total: number) => void;
  /** v1.5.0: repassa os KPIs do servidor (cards do modal do prestador) */
  onKpisChange?: (kpis: ServiceOrderKpis) => void;
}

export function ServiceOrdersEmbeddedList({ vehicleId = null, customerId = null, providerId = null, ids = null, dealId = null, onTotalChange, onKpisChange }: Props) {
  const [filters, setFilters] = useState<ServiceOrdersFilters>({
    search: '', status: null, serviceTypeId: null, providerId, categoryId: null, phaseId: null,
    deadlineBucket: null, dateField: 'created_at', dateFrom: null, dateTo: null,
    sortField: 'created_at', sortDirection: 'desc', page: 1, pageSize: 10, vehicleId, customerId, ids, dealId,
  });
  const [searchInput, setSearchInput] = useState('');
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const { data, isLoading, isFetching, refetch } = useServiceOrdersList(filters);

  // Busca com espera de 350 ms (mesmo padrão da listagem)
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Total sem filtro de situação/busca (KPI "total" do servidor) alimenta o contador
  useEffect(() => { if (data?.kpis) { onTotalChange?.(data.kpis.total); onKpisChange?.(data.kpis); } }, [data?.kpis, onTotalChange, onKpisChange]);

  const sort = (field: ServiceOrderSortField) =>
    setFilters((f) => ({ ...f, sortField: field, sortDirection: f.sortField === field && f.sortDirection === 'desc' ? 'asc' : 'desc', page: 1 }));

  /** Aplica o filtro de situação (null = Todas) e volta para a primeira página. */
  const setStatus = (status: StatusFilter | null) =>
    setFilters((f) => ({ ...f, status, page: 1 }));

  // Contadores por situação vindos do servidor (KPIs da listagem, sem nova consulta)
  const kpis = data?.kpis;
  const countOf = (k: StatusFilter | 'all') =>
    k === 'all' ? kpis?.total ?? 0 : kpis?.[k] ?? 0;

  /** Verdadeiro quando um filtro do menu "..." está ativo (acende a bolinha no botão). */
  const hiddenFilterActive = filters.status === 'overdue' || HIDDEN_STATUS.includes(filters.status as ServiceOrderStatus);

  /** Botão pílula no padrão da aba Vistorias (bolinha + texto + contador). */
  const Pill = ({ active, onClick, dot, label, count }: { active: boolean; onClick: () => void; dot?: string; label: string; count?: number }) => (
    <Button variant={active ? 'secondary' : 'ghost'} size="sm" className="h-6 text-xs px-2" onClick={onClick}>
      {dot && <span className={cn('w-1.5 h-1.5 rounded-full mr-1', dot)} />}
      {label}{count !== undefined && <span className="ml-1 text-[10px] text-muted-foreground">{count}</span>}
    </Button>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-3">
        <Input className={cn(LIST_FILTERS_INPUT_CLASS, 'min-w-0 flex-1 sm:max-w-xs')} value={searchInput} maxLength={100}
          placeholder="Buscar número, prestador..." onChange={(e) => setSearchInput(e.target.value)} />
        {/* Filtro de situação em pílulas (padrão da aba Vistorias): em aberto visíveis, finais no "..." */}
        {/* v1.6.0: pílulas alinhadas à direita, coladas ao botão Atualizar */}
        <div className="ml-auto flex flex-wrap items-center gap-1">
          <Pill active={!filters.status} onClick={() => setStatus(null)} label="Todas" count={countOf('all')} />
          {VISIBLE_STATUS.map((k) => (
            <Pill key={k} active={filters.status === k} onClick={() => setStatus(k)} dot={STATUS_DOT[k]} label={SERVICE_ORDER_STATUS_LABEL[k]} count={countOf(k)} />
          ))}
          <TooltipProvider>
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="relative h-7 w-7 shrink-0" aria-label="Mais filtros">
                      <MoreHorizontal className="h-4 w-4" />
                      {hiddenFilterActive && <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />}
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>Mais filtros</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="start" className="w-56">
                {/* Estados finais: seleção exclusiva, no padrão das pílulas */}
                {HIDDEN_STATUS.map((k) => (
                  <DropdownMenuItem key={k} className="gap-2" onSelect={() => setStatus(k)}>
                    <span className={cn('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[k])} />
                    <span className="flex-1">{SERVICE_ORDER_STATUS_LABEL[k]}</span>
                    <span className="text-[10px] tabular-nums text-muted-foreground">{countOf(k)}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem className="gap-2" onSelect={() => setStatus('overdue')}>
                  <span className="h-2 w-2 shrink-0 rounded-full bg-destructive" />
                  <span className="flex-1">Atrasadas</span>
                  <span className="text-[10px] tabular-nums text-muted-foreground">{countOf('overdue')}</span>
                </DropdownMenuItem>
                {hiddenFilterActive && <DropdownMenuSeparator />}
                {hiddenFilterActive && (
                  <DropdownMenuItem onSelect={() => setStatus(null)}>
                    <RotateCcw className="mr-2 h-3.5 w-3.5" />Limpar filtros
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipProvider>
        </div>
        {/* Total removido: o contador da aba e a pílula "Todas" já informam a quantidade de OS. */}
        <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => refetch()} disabled={isFetching} title="Atualizar ordens de serviço" aria-label="Atualizar ordens de serviço">
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <ServiceOrdersTable items={data?.items ?? []} isLoading={isLoading} sortField={filters.sortField}
          sortDirection={filters.sortDirection} onSort={sort} onOpen={setDetailsId} />
      </div>
      <div className="border-t px-4 py-2">
        <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
          pageSizeOptions={[10, 25, 50]} borderClassName="" className="mt-0 pt-0" />
      </div>
      <ServiceOrderDetailsModal open={!!detailsId} onOpenChange={(o) => !o && setDetailsId(null)} serviceOrderId={detailsId} />
    </div>
  );
}
